import { ButtonHTMLAttributes, ReactNode, forwardRef } from "react";
import styles from "./Button.module.css";

type Variant =
  | "primary"
  | "secondary"
  | "ghost"
  | "danger"
  | "danger-outline";
type Size = "sm" | "md" | "lg" | "icon";

interface Props extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant;
  size?: Size;
  loading?: boolean;
  icon?: ReactNode;
}

// Translate variant string into the CSS-module class. `danger-outline` maps to
// `dangerOutline` since CSS-modules class names cannot contain a hyphen.
const SIZE_CLASS: Record<Size, string> = {
  sm: "sm",
  md: "md",
  lg: "lg",
  // Not `icon`: that class is already the span wrapping the `icon` prop.
  icon: "iconSquare",
};

const VARIANT_CLASS: Record<Variant, string> = {
  primary: "primary",
  secondary: "secondary",
  ghost: "ghost",
  danger: "danger",
  "danger-outline": "dangerOutline",
};

export const Button = forwardRef<HTMLButtonElement, Props>(
  (
    {
      variant = "primary",
      size = "md",
      loading = false,
      icon,
      className = "",
      children,
      disabled,
      ...rest
    },
    ref,
  ) => {
    const variantCls = styles[VARIANT_CLASS[variant]];
    const cls = [
      styles.btn,
      variantCls,
      styles[SIZE_CLASS[size]],
      loading ? styles.loading : "",
      className,
    ]
      .filter(Boolean)
      .join(" ");
    return (
      <button
        ref={ref}
        className={cls}
        disabled={disabled || loading}
        aria-busy={loading || undefined}
        {...rest}
      >
        {loading && <span className={styles.spinner} aria-hidden="true" />}
        {!loading && icon && (
          <span className={styles.icon} aria-hidden="true">
            {icon}
          </span>
        )}
        {children}
      </button>
    );
  },
);
Button.displayName = "Button";
